import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  Circle,
  X,
  Calendar,
  User as UserIcon,
  Eye,
  MessageSquare,
} from "lucide-react";
import api from "../../utils/api";
import { toast } from "sonner";

export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [filterCompleted, setFilterCompleted] = useState("");

  const renderText = (text) => {
    if (!text) return "";
    if (typeof text === "string") return text;
    return text.en || text.ar || Object.values(text)[0] || "";
  };

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);

  const [notesList, setNotesList] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    lead_id: "",
    assigned_to: "",
    due_date: "",
    notes: "",
  });

  const currentUser = JSON.parse(localStorage.getItem("admin_user") || "{}");
  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    fetchTasks();
  }, [page, filterCompleted]);

  useEffect(() => {
    fetchAuxData();
  }, []);

  const fetchTasks = async () => {
    try {
      setIsLoading(true);
      const params = { page };
      if (filterCompleted !== "") params.is_completed = filterCompleted;

      const { data } = await api.get("/admin/tasks", { params });
      setTasks(data.data);
      setTotalPages(data.meta?.last_page || 1);
    } catch (error) {
      toast.error("Failed to load tasks");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAuxData = async () => {
    try {
      const [leadsRes, usersRes] = await Promise.all([
        api.get("/admin/leads?per_page=100"),
        isAdmin
          ? api.get("/admin/users?per_page=100")
          : Promise.resolve({ data: { data: [currentUser] } }),
      ]);
      setLeads(leadsRes.data.data || []);
      setUsers(usersRes.data.data || []);
    } catch (error) {
      console.error("Failed to load aux data", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const fetchNotes = async (taskId) => {
    try {
      const { data } = await api.get(`/admin/tasks/${taskId}/notes`);
      setNotesList(data.data);
    } catch (error) {
      toast.error("Failed to load notes");
    }
  };

  const handleOpenDetail = (task) => {
    setCurrentTask(task);
    fetchNotes(task.id);
    setIsDetailOpen(true);
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/tasks/${currentTask.id}/notes`, {
        content: newNote,
      });
      setNewNote("");
      fetchNotes(currentTask.id);
      toast.success("Note added");
    } catch (err) {
      toast.error("Failed to add note");
    }
  };

  const handleOpenForm = (task = null) => {
    if (task) {
      setCurrentTask(task);
      setFormData({
        title: task.title || "",
        description: task.description || "",
        lead_id: task.lead?.id || "",
        assigned_to: task.assigned_user?.id || "",
        due_date: task.due_date || "",
        notes: task.notes || "",
      });
    } else {
      setCurrentTask(null);
      setFormData({
        title: "",
        description: "",
        lead_id: "",
        assigned_to: isAdmin ? "" : currentUser.id,
        due_date: "",
        notes: "",
      });
    }
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.lead_id) delete payload.lead_id;
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.due_date) delete payload.due_date;

      if (currentTask) {
        await api.put(`/admin/tasks/${currentTask.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/admin/tasks", payload);
        toast.success("Task created");
      }
      setIsFormOpen(false);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving task");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await api.delete(`/admin/tasks/${id}`);
      toast.success("Task deleted");
      fetchTasks();
    } catch (err) {
      toast.error("Error deleting task");
    }
  };

  const toggleComplete = async (id, currentStatus) => {
    try {
      await api.post(`/admin/tasks/${id}/toggle-complete`);
      toast.success(
        currentStatus ? "Task marked incomplete" : "Task marked complete",
      );
      fetchTasks();
    } catch (err) {
      toast.error("Error updating task status");
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Task Management</h1>
          <p className="text-gray-500 text-sm mt-1">
            Track follow-ups, calls, and to-do items
          </p>
        </div>

        <button
          onClick={() => handleOpenForm()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus size={20} />
          New Task
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex gap-4">
        <select
          value={filterCompleted}
          onChange={(e) => setFilterCompleted(e.target.value)}
          className="w-full sm:w-48 pl-4 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none"
        >
          <option value="">All Tasks</option>
          <option value="0">Pending</option>
          <option value="1">Completed</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-full py-12 text-center text-gray-500">
            Loading tasks...
          </div>
        ) : tasks.length === 0 ? (
          <div className="col-span-full py-12 text-center text-gray-500 bg-white rounded-xl border border-gray-100 border-dashed">
            No tasks found. Create one to get started.
          </div>
        ) : (
          tasks.map((task) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`bg-white rounded-xl border shadow-sm p-5 relative overflow-hidden transition-all duration-300 ${task.is_completed ? "border-gray-200 bg-gray-50" : "border-blue-100 hover:shadow-md hover:border-blue-200"}`}
            >
              <div className="flex gap-4 items-start">
                <button
                  onClick={() => toggleComplete(task.id, task.is_completed)}
                  className={`mt-1 shrink-0 transition-colors ${task.is_completed ? "text-emerald-500 hover:text-gray-400" : "text-gray-300 hover:text-emerald-500"}`}
                >
                  {task.is_completed ? (
                    <CheckCircle size={24} />
                  ) : (
                    <Circle size={24} />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <h3
                    className={`font-semibold text-lg truncate ${task.is_completed ? "text-gray-500 line-through" : "text-gray-900"}`}
                  >
                    {task.title}
                  </h3>

                  {task.lead && (
                    <p
                      className={`text-sm mt-1 mb-2 ${task.is_completed ? "text-gray-400" : "text-blue-600 font-medium"}`}
                    >
                      Lead: {renderText(task.lead.name)}
                    </p>
                  )}

                  {task.description && (
                    <p
                      className={`text-sm line-clamp-2 mt-1 ${task.is_completed ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {task.description}
                    </p>
                  )}

                  <div className="flex items-center gap-4 mt-4 text-xs font-medium text-gray-500">
                    {task.due_date && (
                      <div
                        className={`flex items-center gap-1.5 ${!task.is_completed && new Date(task.due_date) < new Date() ? "text-red-500" : ""}`}
                      >
                        <Calendar size={14} />
                        {task.due_date}
                      </div>
                    )}

                    {task.assigned_user && (
                      <div className="flex items-center gap-1.5">
                        <UserIcon size={14} />
                        {task.assigned_user.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="absolute top-4 right-4 flex opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Always show actions on small screens, map via CSS hover is tricky without group class, so we just show them always visible on mobile and desktop for best UX */}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end gap-2">
                <button
                  onClick={() => handleOpenDetail(task)}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                  title="View Details"
                >
                  <Eye size={16} />
                </button>
                {(isAdmin || (currentUser?.id && task.created_by === currentUser.id)) && (
                  <>
                    <button
                      onClick={() => handleOpenForm(task)}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition"
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(task.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center bg-transparent mt-6">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition"
          >
            Previous
          </button>
          <span className="text-sm font-medium text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition"
          >
            Next
          </button>
        </div>
      )}

      {/* Form Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-bold text-gray-900">
                  {currentTask ? "Edit Task" : "New Task"}
                </h2>
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Task Title *
                  </label>
                  <input
                    required
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="e.g. Call family member"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Related Lead
                  </label>
                  <select
                    name="lead_id"
                    value={formData.lead_id}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">None</option>
                    {leads.map((l) => (
                      <option key={l.id} value={l.id}>
                        {renderText(l.name)}
                      </option>
                    ))}
                  </select>
                </div>

                {isAdmin && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Assign To
                    </label>
                    <select
                      name="assigned_to"
                      value={formData.assigned_to}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Unassigned</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Due Date
                  </label>
                  <input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    rows={2}
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  ></textarea>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Private Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={2}
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  ></textarea>
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setIsFormOpen(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
                  >
                    {currentTask ? "Save Changes" : "Create Task"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {isDetailOpen && currentTask && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-gray-50 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shrink-0 w-full">
                <div className="flex-1 min-w-0 pr-4">
                  <h2 className="text-xl font-bold text-gray-900 truncate">
                    {currentTask.title}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        currentTask.is_completed
                          ? "bg-emerald-500"
                          : "bg-amber-500"
                      }`}
                    ></span>
                    {currentTask.is_completed ? "Completed" : "Pending"}
                  </p>
                </div>
                <button
                  onClick={() => setIsDetailOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 shrink-0"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 flex flex-col md:flex-row gap-6 w-full max-w-full">
                {/* Info sidebar */}
                <div className="w-full md:w-1/3 shrink-0 space-y-6">
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm w-full">
                    <h3 className="font-semibold text-gray-900 mb-3 border-b pb-2">
                      Task Details
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-gray-500 block text-xs">
                          Assigned To
                        </span>
                        <div className="font-medium text-gray-900 mt-0.5 break-words">
                          {currentTask.assigned_user?.name || "Unassigned"}
                        </div>
                      </div>
                      {currentTask.due_date && (
                        <div>
                          <span className="text-gray-500 block text-xs">
                            Due Date
                          </span>
                          <div className="font-medium text-gray-900 mt-0.5 break-words">
                            {new Date(
                              currentTask.due_date,
                            ).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                      {currentTask.lead && (
                        <div>
                          <span className="text-gray-500 block text-xs">
                            Related Lead
                          </span>
                          <div className="font-medium text-blue-600 mt-0.5 break-words hover:underline cursor-pointer">
                            {renderText(currentTask.lead.name)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {(currentTask.description || currentTask.notes) && (
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 w-full">
                      {currentTask.description && (
                        <div className="mb-3">
                          <h3 className="font-semibold text-blue-900 mb-1 text-xs uppercase tracking-wider">
                            Description
                          </h3>
                          <p className="text-sm text-blue-800 whitespace-pre-wrap break-words">
                            {currentTask.description}
                          </p>
                        </div>
                      )}
                      {currentTask.notes && (
                        <div>
                          <h3 className="font-semibold text-blue-900 mb-1 text-xs uppercase tracking-wider">
                            Private Notes
                          </h3>
                          <p className="text-sm text-blue-800 whitespace-pre-wrap break-words">
                            {currentTask.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Notes/Activity Section */}
                <div className="w-full md:w-2/3 flex flex-col min-h-[400px] border border-gray-200 rounded-xl bg-gray-50 overflow-hidden">
                  <div className="bg-white px-4 py-3 border-b border-gray-200 shrink-0">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <MessageSquare size={18} className="text-blue-500" />{" "}
                      Activity & Notes
                    </h3>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-full">
                    {notesList.length === 0 ? (
                      <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-gray-200 border-dashed max-w-sm mx-auto">
                        <MessageSquare
                          size={32}
                          className="mx-auto mb-3 opacity-20"
                        />
                        <p className="text-sm">No activity notes yet.</p>
                      </div>
                    ) : (
                      notesList.map((note) => (
                        <div
                          key={note.id}
                          className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm relative w-full"
                        >
                          <div className="flex justify-between items-start mb-2 gap-4">
                            <span className="font-medium text-sm text-gray-900 truncate">
                              {note.user?.name}
                            </span>
                            <span className="text-xs text-gray-500 shrink-0">
                              {new Date(note.created_at).toLocaleString([], {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                            {note.content}
                          </p>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="p-4 bg-white border-t border-gray-200 shrink-0 w-full">
                    <form
                      onSubmit={handleAddNote}
                      className="flex gap-2 w-full"
                    >
                      <input
                        type="text"
                        placeholder="Add a new note..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        className="flex-1 min-w-0 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition text-sm"
                      />
                      <button
                        type="submit"
                        disabled={!newNote.trim()}
                        className="shrink-0 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition"
                      >
                        Add Note
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
